import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useFetchThreadAttachments } from '../hooks'
import { useAppSelector } from '../../../store/hooks'
import { selectEmailThreadAttachments, selectEmailThreadAttachmentsLoading } from '../../../store/emailHub/emailAttachments'
import { IntegrationEmailAttachment } from '../../../models/EmailAttachments'
import { User } from '../../../models/User'
import { Overlay, Spinner } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { UserAvatar } from '../../../components/UserAvatar/UserAvatar'
import { selectContactsForThread } from '../../../store/emailHub/emailUsers'
import './EmailThreadAttachments.scss'
import { Divider } from '../../../components/Divider/Divider'
import BootstrapTooltip from 'react-bootstrap/Tooltip'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { formatExpressiveDate } from '../../../common/dateUtils'
import { useLoadEmailAttachmentMutation } from '../../../store/integrations/integrations'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useCreateDocumentMutation } from '../../../store/ai/documents'
import { useCreateDocumentBatchMutation } from '../../../store/ai/documentBatches'
import { DocumentUploadSource, uploadQueueItemToDocumentCreate, uploadQueueItemsToDocumentBatchCreate } from '../../ProcessDocument/DocumentUpload/utils'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

const _COLLAPSED_ATTACHMENT_COUNT = 3

export interface EmailThreadAttachmentsProps {
  emailConnector: string
  threadId: string
}

export const EmailThreadAttachments: React.FC<EmailThreadAttachmentsProps> = ({ emailConnector, threadId }) => {
  const { fetchThreadAttachments } = useFetchThreadAttachments()

  const accountId = useAppSelector(selectAccountId)
  const isLoading = useAppSelector(selectEmailThreadAttachmentsLoading)
  const attachments = useAppSelector(selectEmailThreadAttachments)

  const contactsForThread = useAppSelector(selectContactsForThread(threadId))

  const { tracked } = useTracking()

  const [triggerLoadAttachment] = useLoadEmailAttachmentMutation()
  const [triggerCreateDocument] = useCreateDocumentMutation()
  const [triggerCreateDocumentBatch] = useCreateDocumentBatchMutation()

  const [attachmentProcessingById, setAttachmentProcessingById] = useState<Record<string, boolean>>({})
  const [isBatchProcessing, setIsBatchProcessing] = useState(false)

  useEffect(() => {
    if (!emailConnector || !threadId) {
      return
    }
    fetchThreadAttachments(emailConnector, threadId)
  }, [emailConnector, threadId, fetchThreadAttachments])

  const loadAttachmentFile = useCallback(
    async (attachment: IntegrationEmailAttachment) =>
      await triggerLoadAttachment({ accountId, connector: emailConnector, emailId: attachment.emailExternalId, attachmentId: attachment.externalId }).unwrap(),
    [accountId, emailConnector, triggerLoadAttachment],
  )

  const _handleProcessAttachment = useCallback(
    async (attachment: IntegrationEmailAttachment, contact: User) => {
      setAttachmentProcessingById((prev) => ({ ...prev, [attachment.externalId]: true }))
      try {
        const fileInfo = await loadAttachmentFile(attachment)
        const payload = uploadQueueItemToDocumentCreate({ source: DocumentUploadSource.ZOCKS, fileInfo }, contact.id)

        const document = await triggerCreateDocument({ accountId, payload }).unwrap()

        window.open(`/dashboard/documents/${document.id}`, '_blank', 'noopener noreferrer')
      } catch (e) {
        console.error(e)
        Sentry.captureException(e)
      } finally {
        setAttachmentProcessingById((prev) => ({ ...prev, [attachment.externalId]: false }))
      }
    },
    [accountId, loadAttachmentFile, triggerCreateDocument],
  )

  const handleProcessAttachment = useMemo(
    () =>
      tracked(_handleProcessAttachment, {
        eventName: TrackEvent.PROCESS_DOCUMENTS_CLICKED,
        eventDetails: { documentCount: 1, source: 'email_attachment', context: 'web' },
      }),
    [_handleProcessAttachment, tracked],
  )

  const _handleProcessAllAttachments = useCallback(
    async (attachments: IntegrationEmailAttachment[], contact: User) => {
      setIsBatchProcessing(true)
      try {
        const fileInfos = await Promise.all(attachments.map(loadAttachmentFile))
        const payload = uploadQueueItemsToDocumentBatchCreate(
          fileInfos.map((fileInfo) => ({ source: DocumentUploadSource.ZOCKS, fileInfo })),
          contact.id,
        )

        const documentBatch = await triggerCreateDocumentBatch({ accountId, payload }).unwrap()

        window.open(`/dashboard/document-batches/${documentBatch.id}`, '_blank', 'noopener noreferrer')
      } catch (e) {
        console.error(e)
        Sentry.captureException(e)
      } finally {
        setIsBatchProcessing(false)
      }
    },
    [accountId, loadAttachmentFile, triggerCreateDocumentBatch],
  )

  const handleProcessAllAttachments = useMemo(
    () =>
      tracked(
        _handleProcessAllAttachments,
        {
          eventName: TrackEvent.PROCESS_DOCUMENTS_CLICKED,
          eventDetails: { source: 'email_attachment', context: 'web' },
        },
        { eventDetailsExtractor: (attachments: IntegrationEmailAttachment[]) => ({ documentCount: attachments.length }) },
      ),
    [_handleProcessAllAttachments, tracked],
  )

  return (
    <>
      {attachments.length > 0 ? (
        <DocumentIntelligencePanel
          attachments={attachments}
          contacts={contactsForThread}
          attachmentsLoading={isLoading}
          onProcessAttachment={handleProcessAttachment}
          onProcessAllAttachments={handleProcessAllAttachments}
          attachmentProcessingById={attachmentProcessingById}
          isBatchProcessing={isBatchProcessing}
        />
      ) : null}
    </>
  )
}

type PendingAction = { type: 'single'; attachment: IntegrationEmailAttachment } | { type: 'all' }

interface DocumentIntelligencePanelProps {
  attachments: IntegrationEmailAttachment[]
  contacts: User[]
  attachmentsLoading: boolean
  onProcessAttachment: (attachment: IntegrationEmailAttachment, contact: User) => void
  onProcessAllAttachments: (attachments: IntegrationEmailAttachment[], contact: User) => void
  attachmentProcessingById: Record<string, boolean>
  isBatchProcessing: boolean
}

const DocumentIntelligencePanel: React.FC<DocumentIntelligencePanelProps> = ({
  attachments,
  contacts,
  attachmentsLoading,
  onProcessAttachment,
  onProcessAllAttachments,
  attachmentProcessingById,
  isBatchProcessing,
}) => {
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)
  const [showContactSelector, setShowContactSelector] = useState(false)
  const [overlayTarget, setOverlayTarget] = useState<HTMLButtonElement | null>(null)
  const [showAllAttachments, setShowAllAttachments] = useState(false)

  const processAllButtonRef = useRef<HTMLButtonElement>(null)

  const sortedAttachments = useMemo(
    () =>
      [...attachments].sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      }),
    [attachments],
  )

  const isAnyProcessing = isBatchProcessing || Object.values(attachmentProcessingById).some(Boolean)

  const handleAttachmentAction = useCallback(
    (attachment: IntegrationEmailAttachment, target: HTMLButtonElement) => {
      if (contacts.length === 1) {
        onProcessAttachment(attachment, contacts[0])
        return
      }
      setPendingAction({ type: 'single', attachment })
      setOverlayTarget(target)
      setShowContactSelector(true)
    },
    [contacts, onProcessAttachment],
  )

  const handleProcessAllAction = useCallback(() => {
    const target = processAllButtonRef.current
    if (!target) {
      return
    }
    if (contacts.length === 1) {
      onProcessAllAttachments(sortedAttachments, contacts[0])
      return
    }
    setPendingAction({ type: 'all' })
    setOverlayTarget(target)
    setShowContactSelector(true)
  }, [contacts, onProcessAllAttachments, sortedAttachments])

  const handleContactSelect = useCallback(
    (contact: User) => {
      if (pendingAction?.type === 'single') {
        onProcessAttachment(pendingAction.attachment, contact)
      } else if (pendingAction?.type === 'all') {
        onProcessAllAttachments(sortedAttachments, contact)
      }
      setShowContactSelector(false)
      setPendingAction(null)
      setOverlayTarget(null)
    },
    [pendingAction, onProcessAttachment, onProcessAllAttachments, sortedAttachments],
  )

  const handleCloseOverlay = () => {
    setShowContactSelector(false)
    setPendingAction(null)
    setOverlayTarget(null)
  }

  const displayedAttachments = showAllAttachments ? sortedAttachments : sortedAttachments.slice(0, _COLLAPSED_ATTACHMENT_COUNT)
  const remainingCount = sortedAttachments.length - _COLLAPSED_ATTACHMENT_COUNT

  return (
    <div className='p-4 zocks-border-0.5 border d-flex flex-column gap-4 zocks-bg-color-white'>
      <div className='d-flex align-items-center justify-content-between'>
        <span className='paragraph-large bold'>Document Intelligence</span>
      </div>
      <div className='d-flex flex-column gap-2'>
        {attachmentsLoading ? (
          <div className='d-flex justify-content-center align-items-center'>
            <Spinner />
          </div>
        ) : (
          <>
            {displayedAttachments.map((attachment, index) => (
              <React.Fragment key={attachment.externalId}>
                <AttachmentDisplay
                  attachment={attachment}
                  onAction={handleAttachmentAction}
                  isProcessing={attachmentProcessingById[attachment.externalId]}
                  disabled={isAnyProcessing}
                />
                {index !== displayedAttachments.length - 1 ? <Divider className='p-0' /> : null}
              </React.Fragment>
            ))}
            {!showAllAttachments && remainingCount > 0 ? (
              <Button variant='link' className='button-link-small text-decoration-none align-self-start py-1' onClick={() => setShowAllAttachments(true)}>
                <span className='zocks-text-primary'>
                  Show {remainingCount} more {remainingCount === 1 ? 'attachment' : 'attachments'}
                </span>
              </Button>
            ) : null}
            {sortedAttachments.length > 1 ? (
              <Button ref={processAllButtonRef} variant='header' className='py-2 mt-1' onClick={handleProcessAllAction} disabled={isAnyProcessing}>
                {isBatchProcessing ? (
                  <span className='d-flex align-items-center justify-content-center gap-2'>
                    <Spinner size='sm' />
                    <span className='paragraph-small bold'>Processing attachments…</span>
                  </span>
                ) : (
                  <span className='paragraph-small bold'>Process all attachments</span>
                )}
              </Button>
            ) : null}
          </>
        )}
      </div>
      {showContactSelector && overlayTarget ? (
        <AttachmentContactSelectorOverlay contacts={contacts} target={overlayTarget} onSelectContact={handleContactSelect} onClose={handleCloseOverlay} />
      ) : null}

      <div className='flex-grow-0 d-flex gap-3 px-1 align-items-center'>
        <i className='bi bi-info-circle' />
        <div className='paragraph-small'>Only PDF, PNG and JPG files up to 20MB.</div>
      </div>
    </div>
  )
}

interface AttachmentDisplayProps {
  attachment: IntegrationEmailAttachment
  onAction: (attachment: IntegrationEmailAttachment, target: HTMLButtonElement) => void
  isProcessing?: boolean
  disabled?: boolean
}

const AttachmentDisplay: React.FC<AttachmentDisplayProps> = ({ attachment, onAction, isProcessing = false, disabled = false }) => {
  const buttonRef = useRef<HTMLButtonElement>(null)

  const tooltipTitle = 'Process attachment to extract insights and update external systems.'

  const handleClick = useCallback(() => {
    if (buttonRef.current) {
      onAction(attachment, buttonRef.current)
    }
  }, [attachment, onAction])

  return (
    <div className='attachment-display d-flex align-items-center gap-3 flex-nowrap'>
      <div className='attachment-badge flex-shrink-0'>
        <i className='bi bi-file-earmark-richtext fs-5' />
      </div>
      <div className='attachment-content flex-grow-1 flex-shrink-1 d-flex flex-column gap-1'>
        <div className='attachment-name paragraph-small bold text-truncate'>{attachment.fileName}</div>
        <ExpressiveDateDisplay date={attachment.createdAt} className='zocks-text-gray-600 paragraph-small' />
      </div>
      <Tooltip className='paragraph-small attachment-display-button-tooltip' title={tooltipTitle} placement={'top'}>
        <Button
          ref={buttonRef}
          variant='header'
          className='attachment-process px-2 py-1 flex-shrink-0 align-self-center'
          onClick={handleClick}
          disabled={isProcessing || disabled}
        >
          {isProcessing ? <Spinner size='sm' /> : <span className='paragraph-small bold'>Process</span>}
        </Button>
      </Tooltip>
    </div>
  )
}

interface AttachmentContactSelectorOverlayProps {
  contacts: User[]
  target: HTMLButtonElement
  onSelectContact: (client: User) => void
  onClose: () => void
}

const AttachmentContactSelectorOverlay: React.FC<AttachmentContactSelectorOverlayProps> = ({ contacts, target, onSelectContact, onClose }) => {
  const sortedContacts = useMemo(() => [...contacts].sort((a, b) => getUserDisplayName(a).localeCompare(getUserDisplayName(b))), [contacts])
  return (
    <Overlay show={true} target={target} placement='top' rootClose onHide={onClose}>
      {(props) => (
        <BootstrapTooltip className='client-selector-overlay' {...props}>
          <div className='d-flex flex-column gap-1'>
            <div className='paragraph-small p-1'>Choose who this attachment is for:</div>
            <div className='d-flex flex-column gap-2'>
              {sortedContacts.map((contact) => (
                <AttachmentContactDisplay key={contact.id} contact={contact} onClick={() => onSelectContact(contact)} />
              ))}
            </div>
          </div>
        </BootstrapTooltip>
      )}
    </Overlay>
  )
}

interface AttachmentContactDisplayProps {
  contact: User
  onClick: () => void
}

const AttachmentContactDisplay: React.FC<AttachmentContactDisplayProps> = ({ contact, onClick }) => {
  const contactDisplayName = getUserDisplayName(contact)
  return (
    <Button variant='link' className='text-decoration-none p-1' onClick={onClick}>
      <div className='d-flex gap-2 align-items-center justify-content-between flex-grow-1 flex-shrink-0'>
        <UserAvatar
          width='1.5rem'
          height='1.5rem'
          fontSize='0.75rem'
          className='zocks-bg-color-gray-900 zocks-text-white'
          useInitials={true}
          initialLetters={contactDisplayName[0]}
        />
        <div className='paragraph-medium medium flex-fill'>{contactDisplayName}</div>
        <i className='bi bi-chevron-right flex-shrink-0' />
      </div>
    </Button>
  )
}

const ExpressiveDateDisplay: React.FC<{ date: string; className?: string }> = ({ date, className }) => {
  const formattedDate = useMemo(() => formatExpressiveDate(new Date(date)), [date])

  return <div className={className ?? ''}>{formattedDate}</div>
}
